/**
 * Her veri erişimi asenkron (veritabanı Worker'da). Bu kanca üç durumu
 * ZORUNLU kılar: yükleniyor, hata, veri. Ekranlar üçünü de tasarlar.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { readonly status: "loading"; readonly data: T | undefined; readonly error: undefined }
  | { readonly status: "ready"; readonly data: T; readonly error: undefined }
  | { readonly status: "error"; readonly data: T | undefined; readonly error: unknown };

export interface AsyncResult<T> {
  readonly state: AsyncState<T>;
  readonly reload: () => void;
}

/**
 * `deps` değişince yeniden çeker. Yarış koşullarını sıra numarasıyla eler:
 * geç dönen eski istek yeni sonucu ezmez.
 */
export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading", data: undefined, error: undefined });
  const seq = useRef(0);
  const mounted = useRef(true);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    seq.current += 1;
    const mine = seq.current;
    setState((prev) => ({ status: "loading", data: prev.data, error: undefined }));
    loadRef
      .current()
      .then((data) => {
        if (!mounted.current || mine !== seq.current) return;
        setState({ status: "ready", data, error: undefined });
      })
      .catch((error: unknown) => {
        if (!mounted.current || mine !== seq.current) return;
        setState((prev) => ({ status: "error", data: prev.data, error }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { state, reload };
}

/** Yazma işlemleri: bekliyor / hata durumunu düğmeye bağlamak için. */
export interface MutationResult {
  readonly pending: boolean;
  readonly error: unknown;
  readonly run: (action: () => Promise<void>) => Promise<boolean>;
  readonly clearError: () => void;
}

export function useMutation(): MutationResult {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(undefined);

  const run = useCallback(async (action: () => Promise<void>): Promise<boolean> => {
    setPending(true);
    setError(undefined);
    try {
      await action();
      return true;
    } catch (e: unknown) {
      setError(e);
      return false;
    } finally {
      setPending(false);
    }
  }, []);

  const clearError = useCallback(() => setError(undefined), []);
  return { pending, error, run, clearError };
}
